import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAnalysisStore } from '@/store/analysisStore'
import { useAuthStore } from '@/store/authStore'
import { haptic } from '@/lib/haptics'

// Decorative sparkle with animation (from LoginWelcomePage)
const Sparkle = ({
  className,
  size = 24,
  color = '#FF8C33',
  delay = 0
}: {
  className?: string
  size?: number
  color?: string
  delay?: number
}) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    initial={{ scale: 0, opacity: 0, rotate: -30 }}
    whileInView={{
      scale: [0, 1.3, 1],
      opacity: [0, 1, 0.8],
      rotate: [0, 15, 0]
    }}
    viewport={{ once: true }}
    transition={{
      duration: 0.6,
      delay: delay,
      ease: "easeOut"
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Polaroid frame bursting from center (from LoginWelcomePage)
const BurstingPolaroid = ({
  src,
  size = 60,
  delay = 0,
  targetX,
  targetY,
  rotate = 0
}: {
  src: string
  size?: number
  delay?: number
  targetX: number
  targetY: number
  rotate?: number
}) => (
  <motion.div
    className="absolute"
    style={{
      width: size + 20,
      left: '50%',
      top: '50%',
      marginLeft: -(size + 20) / 2,
      marginTop: -(size + 30) / 2,
    }}
    initial={{
      opacity: 0,
      scale: 0,
      x: 0,
      y: 0,
      rotate: 0
    }}
    whileInView={{
      opacity: [0, 1, 1, 1],
      scale: [0, 1.2, 1],
      x: targetX,
      y: targetY,
      rotate: [0, rotate * 1.5, rotate],
    }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{
      duration: 0.8,
      delay: delay,
      ease: [0.34, 1.56, 0.64, 1],
    }}
  >
    {/* Gentle floating after burst */}
    <motion.div
      animate={{
        y: [0, -8, 0, -5, 0],
        rotate: [0, 3, -2, 2, 0]
      }}
      transition={{
        duration: 4,
        delay: delay + 0.8,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {/* Polaroid frame */}
      <div
        className="bg-white rounded-[3px] p-[5px] pb-[16px] shadow-lg"
        style={{
          boxShadow: '0 4px 15px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'
        }}
      >
        <div className="bg-[#f5f5f5] p-[3px] rounded-[2px]">
          <img
            src={src}
            alt=""
            className="w-full h-auto rounded-[1px]"
            style={{ aspectRatio: '1/1', objectFit: 'cover' }}
          />
        </div>
      </div>
    </motion.div>
  </motion.div>
)

// Product images for polaroids
const PRODUCT_IMAGES = [
  '/image/polaroid-01.jpg',
  '/image/polaroid-02.jpg',
  '/image/polaroid-03.jpg',
  '/image/polaroid-04.jpg',
  '/image/polaroid-05.jpg',
  '/image/polaroid-06.jpg',
  '/image/polaroid-07.jpg',
  '/image/polaroid-08.jpg',
  '/image/polaroid-09.jpg',
  '/image/polaroid-10.jpg',
  '/image/polaroid-11.jpg',
  '/image/polaroid-12.jpg',
  '/image/polaroid-13.jpg',
  '/image/polaroid-14.jpg',
  '/image/polaroid-15.jpg',
  '/image/polaroid-16.jpg',
  '/image/polaroid-17.jpg',
  '/image/polaroid-18.jpg',
  '/image/polaroid-19.jpg',
  '/image/polaroid-20.jpg',
  '/image/polaroid-21.jpg',
  '/image/polaroid-22.jpg',
  '/image/polaroid-23.jpg',
  '/image/polaroid-24.jpg',
  '/image/polaroid-25.jpg',
  '/image/polaroid-26.jpg',
  '/image/polaroid-27.jpg',
  '/image/polaroid-28.jpg',
  '/image/polaroid-29.jpg',
  '/image/polaroid-30.jpg',
]

export function LandingPage() {
  const navigate = useNavigate()
  const { analysisResults } = useAnalysisStore()
  const { hasPaid, demoMode } = useAuthStore()

  // Auto-redirect users with existing results
  const hasRedirected = useRef(false)
  useEffect(() => {
    if (analysisResults && !hasRedirected.current) {
      hasRedirected.current = true
      if (hasPaid || demoMode) {
        navigate('/report', { state: { showWelcomeToast: true } })
      } else {
        navigate('/teaser', { state: { showWelcomeToast: true } })
      }
    }
  }, [analysisResults, hasPaid, demoMode, navigate])

  const [showConfetti, setShowConfetti] = useState(false)

  const handleStartAnalysis = () => {
    haptic('light')
    // Show confetti burst
    setShowConfetti(true)
    setTimeout(() => setShowConfetti(false), 1000)

    // Smart redirect based on user progress
    setTimeout(() => {
      if (analysisResults) {
        if (hasPaid || demoMode) {
          navigate('/report', { state: { showWelcomeToast: true } })
        } else {
          navigate('/teaser', { state: { showWelcomeToast: true } })
        }
      } else {
        navigate('/survey')
      }
    }, 300)
  }

  // Generate confetti pieces
  const confettiColors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA', '#7ED957']
  const confettiPieces = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    color: confettiColors[i % confettiColors.length],
    x: (Math.random() - 0.5) * 300,
    y: (Math.random() - 0.5) * 300 - 100,
    delay: Math.random() * 0.3,
  }))

  return (
    <div className="min-h-dvh relative bg-white overflow-x-hidden">
      {/* CSS Animations */}
      <style>{`
        @keyframes sparkle {
          0%, 100% { opacity: 0.6; transform: scale(1) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.2) rotate(15deg); }
        }
        @keyframes shine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .shine-animation {
          background: linear-gradient(90deg, #E8A87C 0%, #E8A87C 40%, #FFD700 50%, #FFFFFF 55%, #FFD700 60%, #E8A87C 70%, #E8A87C 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shine 3s linear infinite;
        }
        @keyframes button-pulse {
          0%, 100% { box-shadow: 0 4px 15px rgba(126, 217, 87, 0.4); transform: scale(1); }
          50% { box-shadow: 0 6px 25px rgba(126, 217, 87, 0.6), 0 0 40px rgba(126, 217, 87, 0.3); transform: scale(1.02); }
        }
        @keyframes button-pulse-coral {
          0%, 100% { box-shadow: 0 4px 15px rgba(232, 118, 107, 0.4); transform: scale(1); }
          50% { box-shadow: 0 6px 25px rgba(232, 118, 107, 0.6), 0 0 40px rgba(232, 118, 107, 0.3); transform: scale(1.02); }
        }
        .sparkle-animation { animation: sparkle 2s ease-in-out infinite; }
        .sparkle-animation-delay-1 { animation: sparkle 2s ease-in-out infinite 0.3s; }
        .sparkle-animation-delay-2 { animation: sparkle 2s ease-in-out infinite 0.6s; }
        .sparkle-animation-delay-3 { animation: sparkle 2s ease-in-out infinite 0.9s; }
        .button-pulse-animation { animation: button-pulse 2s ease-in-out infinite; }
        .button-pulse-coral-animation { animation: button-pulse-coral 2s ease-in-out infinite; }

        /* Animated gradient background */
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animated-gradient {
          background: linear-gradient(-45deg, #F5D0C5, #FFE4E1, #FFFEF1, #E8F4F8, #F5D0C5);
          background-size: 400% 400%;
          animation: gradient-shift 8s ease infinite;
        }

        /* Floating animation for elements around model */
        @keyframes float-up-down {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes float-diagonal {
          0%, 100% { transform: translate(0px, 0px) rotate(0deg); }
          50% { transform: translate(10px, -10px) rotate(10deg); }
        }
        @keyframes float-circle {
          0% { transform: rotate(0deg) translateX(10px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(10px) rotate(-360deg); }
        }
        .float-up-down { animation: float-up-down 3s ease-in-out infinite; }
        .float-diagonal { animation: float-diagonal 4s ease-in-out infinite; }
        .float-circle { animation: float-circle 8s linear infinite; }

        /* Slow rotating starburst behind model */
        @keyframes slow-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .slow-rotate { animation: slow-rotate 30s linear infinite; }

        /* Arrow bounce animation */
        @keyframes arrow-bounce {
          0%, 100% { transform: translateX(0); opacity: 0.7; }
          50% { transform: translateX(5px); opacity: 1; }
        }
        .arrow-bounce { animation: arrow-bounce 1.5s ease-in-out infinite; }

        /* Checkmark pop animation */
        @keyframes check-pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        .check-pop { animation: check-pop 0.3s ease-out forwards; }

        /* Floating dots animation */
        @keyframes float-dot {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-10px) rotate(180deg); opacity: 0.6; }
        }
        .float-dot { animation: float-dot 4s ease-in-out infinite; }

        /* Card 3D tilt on hover */
        .card-3d {
          transform-style: preserve-3d;
          transition: transform 0.3s ease;
        }
        .card-3d:hover {
          transform: perspective(1000px) rotateY(-5deg) rotateX(5deg) scale(1.05);
        }

        /* Hand-drawn border effect */
        .hand-drawn {
          border: 2px solid transparent;
          border-radius: 16px;
          background-clip: padding-box;
          position: relative;
        }
        .hand-drawn::before {
          content: '';
          position: absolute;
          inset: -3px;
          border-radius: 18px;
          background: repeating-linear-gradient(
            90deg,
            currentColor 0px,
            currentColor 4px,
            transparent 4px,
            transparent 8px
          );
          opacity: 0.3;
          z-index: -1;
        }

        /* Sparkle twinkle animation */
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        .twinkle { animation: twinkle 1.5s ease-in-out infinite; }

        /* Progress path dash animation */
        @keyframes dash-flow {
          0% { stroke-dashoffset: 20; }
          100% { stroke-dashoffset: 0; }
        }
        .progress-path {
          stroke-dasharray: 8, 8;
          animation: dash-flow 1s linear infinite;
        }

        /* Bubble/circle animations */
        @keyframes bubble-rise {
          0% { transform: translateY(100%) scale(0); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-100vh) scale(1); opacity: 0; }
        }
        .bubble { animation: bubble-rise 10s ease-in-out infinite; }

        /* Emoji rain */
        @keyframes emoji-fall {
          0% { transform: translateY(-100%) rotate(0deg); opacity: 0; }
          10% { opacity: 0.8; }
          90% { opacity: 0.8; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
        .emoji-rain { animation: emoji-fall 12s linear infinite; }

        /* Confetti burst */
        @keyframes confetti-burst {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate(var(--x), var(--y)) rotate(720deg); opacity: 0; }
        }
        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti-burst 1s ease-out forwards;
        }

        /* Product carousel */
        @keyframes scroll-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .carousel-scroll {
          animation: scroll-left 20s linear infinite;
        }
        .carousel-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Hero Section with Animated Gradient */}
      <section className="animated-gradient relative pt-[20px] pb-[30px] px-[20px] min-h-[520px]">
        {/* Starburst decorations - behind text (z-0) */}
        <img
          src="/image/starburst.svg"
          alt=""
          className="absolute top-[80px] left-[15px] w-[50px] h-[50px] sparkle-animation opacity-70 z-0"
        />
        <img
          src="/image/starburst.svg"
          alt=""
          className="absolute top-[150px] left-[40px] w-[30px] h-[30px] sparkle-animation-delay-1 opacity-50 z-0"
        />
        <img
          src="/image/starburst.svg"
          alt=""
          className="absolute top-[280px] right-[20px] w-[35px] h-[35px] sparkle-animation-delay-2 opacity-60 z-0"
        />
        <img
          src="/image/starburst.svg"
          alt=""
          className="absolute top-[350px] left-[25px] w-[25px] h-[25px] sparkle-animation-delay-3 opacity-50 z-0"
        />

        {/* Rotating Starburst behind Model */}
        <div className="absolute top-[10px] right-[-30px] z-[0]">
          <img
            src="/image/starburst.svg"
            alt=""
            className="w-[220px] h-[220px] slow-rotate opacity-80"
          />
        </div>

        {/* Hero Model Image */}
        <div className="absolute top-[70px] right-[10px] z-[1]">
          <img
            src="/image/hero-model.png"
            alt="K-beauty model"
            className="w-[150px] h-auto object-contain"
          />
        </div>

        {/* Animated bubbles/circles in background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bubble absolute rounded-full"
              style={{
                width: `${15 + i * 8}px`,
                height: `${15 + i * 8}px`,
                left: `${15 + i * 15}%`,
                bottom: '-50px',
                background: ['#FFB6C1', '#87CEEB', '#98FB98', '#DDA0DD', '#F0E68C', '#E0FFFF'][i],
                animationDelay: `${i * 2}s`,
                animationDuration: `${8 + i * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Logo */}
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[18px] text-center mb-[15px] relative z-10"
          style={{ fontFamily: "'Oleo Script', cursive" }}
        >
          <span className="text-[#688192]">K-Beauty</span>
          <span className="text-[#46C0C6]"> AI</span>
        </motion.h1>

        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-[20px] mt-[30px] relative z-10"
        >
          <h2
            className="text-[24px] leading-[1.15] tracking-[-0.02em] text-black font-semibold text-left"
            style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
          >
            Get your personalized
            <br />
            <span className="text-[#00B8C1]">K-beauty routine</span>
            <br />
            <span className="text-[18px]">in just 3 simple steps</span>
          </h2>
        </motion.div>
      </section>

      {/* 3 Simple Steps Section */}
      <section className="px-[20px] pt-[20px] pb-[30px] bg-[#FFF5F0] -mt-[300px] relative z-[5] overflow-hidden">
        <motion.h3
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-[20px] text-[#E8A87C] mb-[15px] text-left relative z-10"
          style={{ fontFamily: "'Pattaya', cursive" }}
        >
          3 Simple Steps
        </motion.h3>

        {/* Step Cards - with Progress Path */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex justify-center items-center gap-[20px] mb-[25px] relative z-10"
        >
          {/* Progress Path (1) - Curved dotted line behind cards */}
          <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] h-[80px] z-0" viewBox="0 0 320 80">
            <path
              d="M30,40 Q100,10 160,40 Q220,70 290,40"
              fill="none"
              stroke="#E8C4B8"
              strokeWidth="3"
              className="progress-path"
            />
          </svg>

          {/* Sparkles around cards */}
          <div className="absolute top-[20%] right-[10%] text-[10px] twinkle" style={{ animationDelay: '0.5s' }}>✨</div>
          <div className="absolute bottom-[10%] right-[25%] text-[11px] twinkle" style={{ animationDelay: '0.3s' }}>✨</div>

          {/* Step 1 - Quick Survey */}
          <motion.div
            className="relative group"
            whileTap={{ scale: 0.98 }}
          >
            <div
              className="card-3d w-[100px] h-[145px] rounded-[16px] flex flex-col items-center py-[14px] relative shadow-lg backdrop-blur-sm border-2 border-white/50"
              style={{
                background: 'linear-gradient(135deg, #FFE4D6 0%, #FFF0E8 50%, #FFE4D6 100%)',
                boxShadow: '0 8px 32px rgba(232, 168, 124, 0.25), inset 0 1px 0 rgba(255,255,255,0.6)'
              }}
            >
              {/* Hand-drawn border accent (8) */}
              <div className="absolute inset-0 rounded-[16px] border-2 border-dashed border-[#E8A87C]/30 pointer-events-none" />

              {/* Hover Checkmark */}
              <div className="absolute -top-[8px] -right-[8px] w-[24px] h-[24px] bg-[#7ED957] rounded-full items-center justify-center hidden group-hover:flex check-pop">
                <span className="text-white text-[14px]">✓</span>
              </div>
              <span className="w-[22px] h-[22px] bg-[#E8A87C] rounded-full text-white text-[12px] font-bold flex items-center justify-center mb-[4px] shadow-md">
                1
              </span>
              <span className="text-[40px] my-[3px]">📋</span>
              <p
                className="text-[11px] text-[#8B5A42] text-center font-semibold leading-tight mt-[4px]"
                style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
              >
                Quick<br />Survey
              </p>
            </div>
          </motion.div>

          {/* Step 2 - Take a Selfie */}
          <motion.div
            className="relative group"
            whileTap={{ scale: 0.98 }}
          >
            <div
              className="card-3d w-[100px] h-[145px] rounded-[16px] flex flex-col items-center py-[14px] relative shadow-lg backdrop-blur-sm border-2 border-white/50"
              style={{
                background: 'linear-gradient(135deg, #D6EEF7 0%, #E8F6FB 50%, #D6EEF7 100%)',
                boxShadow: '0 8px 32px rgba(126, 200, 227, 0.25), inset 0 1px 0 rgba(255,255,255,0.6)'
              }}
            >
              {/* Hand-drawn border accent (8) */}
              <div className="absolute inset-0 rounded-[16px] border-2 border-dashed border-[#7EC8E3]/30 pointer-events-none" />

              {/* Hover Checkmark */}
              <div className="absolute -top-[8px] -right-[8px] w-[24px] h-[24px] bg-[#7ED957] rounded-full items-center justify-center hidden group-hover:flex check-pop">
                <span className="text-white text-[14px]">✓</span>
              </div>
              <span className="w-[22px] h-[22px] bg-[#7EC8E3] rounded-full text-white text-[12px] font-bold flex items-center justify-center mb-[4px] shadow-md">
                2
              </span>
              <span className="text-[40px] my-[3px]">🤳</span>
              <p
                className="text-[11px] text-[#2D7A96] text-center font-semibold leading-tight mt-[4px]"
                style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
              >
                Take a<br />Selfie
              </p>
            </div>
          </motion.div>

          {/* Step 3 - Get your K-Routine */}
          <motion.div
            className="relative group"
            whileTap={{ scale: 0.98 }}
          >
            <div
              className="card-3d w-[100px] h-[145px] rounded-[16px] flex flex-col items-center py-[14px] relative shadow-lg backdrop-blur-sm border-2 border-white/50"
              style={{
                background: 'linear-gradient(135deg, #D6F5F5 0%, #E8FAFA 50%, #D6F5F5 100%)',
                boxShadow: '0 8px 32px rgba(70, 192, 198, 0.25), inset 0 1px 0 rgba(255,255,255,0.6)'
              }}
            >
              {/* Hand-drawn border accent (8) */}
              <div className="absolute inset-0 rounded-[16px] border-2 border-dashed border-[#46C0C6]/30 pointer-events-none" />

              {/* Hover Checkmark */}
              <div className="absolute -top-[8px] -right-[8px] w-[24px] h-[24px] bg-[#7ED957] rounded-full items-center justify-center hidden group-hover:flex check-pop">
                <span className="text-white text-[14px]">✓</span>
              </div>
              <span className="w-[22px] h-[22px] bg-[#46C0C6] rounded-full text-white text-[12px] font-bold flex items-center justify-center mb-[4px] shadow-md">
                3
              </span>
              <span className="text-[40px] my-[3px]">🧴</span>
              <p
                className="text-[11px] text-[#1A7A80] text-center font-semibold leading-tight mt-[4px]"
                style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
              >
                Get your<br />K-Routine
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* FREE Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative mb-[20px] z-10 text-center"
        >
          <h3
            className="text-[36px] font-bold mb-[4px] shine-animation"
            style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
          >
            FREE
          </h3>
          <p
            className="text-[16px] font-semibold text-black leading-[1.3]"
            style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            Skin Analysis &
            <br />
            Product Recommendations
          </p>
          <p
            className="text-[13px] text-[#656565] mt-[6px]"
            style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            <span className="font-medium">Free</span> analysis preview. Full report for{' '}
            <span className="line-through">$99</span> →{' '}
            <span className="text-[#46C0C6] font-bold">$29</span>
          </p>
        </motion.div>

        {/* Start Now Button with Confetti */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="relative z-10 flex justify-center"
        >
          <div className="relative">
            <motion.button
              onClick={handleStartAnalysis}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="w-[280px] h-[56px] bg-[#7ED957] rounded-[28px] text-white text-[22px] font-bold button-pulse-animation"
              style={{
                fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              Start Now!
            </motion.button>
            {/* Confetti burst on click */}
            {showConfetti && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {confettiPieces.map((piece) => (
                  <div
                    key={piece.id}
                    className="confetti-piece rounded-sm"
                    style={{
                      backgroundColor: piece.color,
                      '--x': `${piece.x}px`,
                      '--y': `${piece.y}px`,
                      animationDelay: `${piece.delay}s`,
                    } as React.CSSProperties}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </section>

      {/* What's Included Section - with bursting polaroids */}
      <section className="relative min-h-[620px] overflow-hidden">
        {/* Animated gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(-45deg, #E8F8EB, #F0FAFA, #FFFEF1, #E8F8EB)',
            backgroundSize: '400% 400%',
            animation: 'gradient-shift 8s ease infinite'
          }}
        />

        {/* Decorative sparkles */}
        <Sparkle className="absolute top-[40px] right-[25px] z-20" size={24} color="#FF8C33" delay={0.2} />
        <Sparkle className="absolute top-[80px] left-[20px] z-20" size={20} color="#46C0C6" delay={0.4} />
        <Sparkle className="absolute top-[200px] right-[15px] z-20" size={18} color="#4CD964" delay={0.6} />
        <Sparkle className="absolute bottom-[300px] left-[15px] z-20" size={22} color="#7EC8E3" delay={0.5} />
        <Sparkle className="absolute bottom-[150px] right-[30px] z-20" size={16} color="#E8A87C" delay={0.7} />
        <Sparkle className="absolute bottom-[80px] left-[40px] z-20" size={20} color="#46C0C6" delay={0.8} />

        {/* Title */}
        <motion.h3
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-[22px] text-[#E8A87C] text-center pt-[30px] relative z-10"
          style={{ fontFamily: "'Pattaya', cursive", fontStyle: 'italic' }}
        >
          What's included?
        </motion.h3>

        {/* Bursting Polaroids - positioned below title */}
        <div className="absolute inset-0 pointer-events-none z-[5]" style={{ top: '60px' }}>
          {/* Top row - just below title */}
          <BurstingPolaroid src={PRODUCT_IMAGES[0]} size={42} delay={0.1} targetX={-130} targetY={-200} rotate={-15} />
          <BurstingPolaroid src={PRODUCT_IMAGES[1]} size={46} delay={0.15} targetX={-50} targetY={-215} rotate={8} />
          <BurstingPolaroid src={PRODUCT_IMAGES[2]} size={44} delay={0.2} targetX={50} targetY={-210} rotate={-10} />
          <BurstingPolaroid src={PRODUCT_IMAGES[3]} size={40} delay={0.25} targetX={130} targetY={-200} rotate={12} />

          {/* Upper sides */}
          <BurstingPolaroid src={PRODUCT_IMAGES[4]} size={50} delay={0.3} targetX={-150} targetY={-130} rotate={-8} />
          <BurstingPolaroid src={PRODUCT_IMAGES[5]} size={48} delay={0.35} targetX={150} targetY={-125} rotate={10} />

          {/* Middle sides */}
          <BurstingPolaroid src={PRODUCT_IMAGES[6]} size={52} delay={0.4} targetX={-155} targetY={-40} rotate={12} />
          <BurstingPolaroid src={PRODUCT_IMAGES[7]} size={46} delay={0.45} targetX={155} targetY={-35} rotate={-14} />
          <BurstingPolaroid src={PRODUCT_IMAGES[8]} size={48} delay={0.5} targetX={-152} targetY={50} rotate={-6} />
          <BurstingPolaroid src={PRODUCT_IMAGES[9]} size={50} delay={0.55} targetX={152} targetY={55} rotate={8} />

          {/* Lower sides */}
          <BurstingPolaroid src={PRODUCT_IMAGES[10]} size={46} delay={0.6} targetX={-148} targetY={140} rotate={10} />
          <BurstingPolaroid src={PRODUCT_IMAGES[11]} size={44} delay={0.65} targetX={150} targetY={135} rotate={-12} />

          {/* Bottom row */}
          <BurstingPolaroid src={PRODUCT_IMAGES[12]} size={42} delay={0.7} targetX={-120} targetY={210} rotate={-8} />
          <BurstingPolaroid src={PRODUCT_IMAGES[13]} size={46} delay={0.75} targetX={-40} targetY={225} rotate={6} />
          <BurstingPolaroid src={PRODUCT_IMAGES[14]} size={44} delay={0.8} targetX={40} targetY={220} rotate={-10} />
          <BurstingPolaroid src={PRODUCT_IMAGES[15]} size={40} delay={0.85} targetX={120} targetY={210} rotate={12} />
        </div>

        {/* Center text content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[520px] px-[20px] pt-[20px]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-[16px] font-bold text-[#1a1a1a] text-center mb-[40px] mt-[30px]"
            style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
          >
            AI-powered<br />skin analysis
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="text-[16px] font-bold text-[#46C0C6] text-center mb-[40px]"
            style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
          >
            Your personalized<br />K-beauty routine
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="text-[16px] font-bold text-[#1a1a1a] text-center mb-[50px]"
            style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
          >
            Chosen for results,<br />not brands
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="text-[12px] text-[#688192] text-center"
            style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            Your personalized K-beauty routine<br />is just one step away!
          </motion.p>
        </div>
      </section>

      {/* Ready to Discover Section */}
      <section className="px-[20px] pt-[35px] pb-[25px] bg-[#FFF9F5]">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-[24px] font-bold leading-[1.25] text-[#075D61] text-center"
          style={{ fontFamily: "'Anybody', 'SF Pro', sans-serif" }}
        >
          Ready to discover your
          <br />
          perfect routine?
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[13px] text-[#656565] leading-[1.5] mt-[15px] text-center"
          style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          Join thousands who have transformed
          <br />
          their skincare with AI-powered
          <br />
          K-beauty recommendations.
        </motion.p>

        {/* Auto-scrolling Product Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-[25px] -mx-[20px] overflow-hidden"
        >
          <div className="carousel-scroll flex items-center">
            {/* Duplicate images for seamless loop */}
            <img
              src="/image/intro-product-strip.png"
              alt="K-beauty products"
              className="h-[120px] flex-shrink-0 object-contain"
            />
            <img
              src="/image/intro-product-strip.png"
              alt="K-beauty products"
              className="h-[120px] flex-shrink-0 object-contain"
            />
          </div>
        </motion.div>

        {/* Second CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-[25px] flex justify-center"
        >
          <motion.button
            onClick={handleStartAnalysis}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="w-[280px] h-[56px] bg-[#E8766B] rounded-[28px] text-white text-[22px] font-bold button-pulse-coral-animation"
            style={{
              fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            Start Free Skin Analysis
          </motion.button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="px-[20px] pt-[35px] pb-[50px] bg-white">
        {/* Logo */}
        <h1
          className="text-[18px] mb-[8px]"
          style={{ fontFamily: "'Oleo Script', cursive" }}
        >
          <span className="text-[#688192]">K-Beauty</span>
          <span className="text-[#46C0C6]"> AI</span>
        </h1>

        {/* Tagline */}
        <p
          className="text-[13px] text-[#656565] leading-[1.4] mb-[25px]"
          style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          Your AI-Beauty Concierge. Personalized skincare
          <br />
          routines backed by Korea dermatology insights.
        </p>

        {/* Links */}
        <div className="flex gap-[50px] mb-[40px]">
          {/* Company */}
          <div>
            <h4
              className="text-[13px] font-bold text-[#333] mb-[6px]"
              style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              Company
            </h4>
            <nav
              className="text-[13px] text-[#656565] leading-[1.8] space-y-[2px]"
              style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              <a href="https://adayhealth.com" target="_blank" rel="noopener noreferrer" className="block hover:underline">
                About us
              </a>
              <a href="#how-it-works" className="block hover:underline">
                How it works
              </a>
              <a href="mailto:contact@adayhealth.com" className="block hover:underline">
                Contact
              </a>
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h4
              className="text-[13px] font-bold text-[#333] mb-[6px]"
              style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              Legal
            </h4>
            <nav
              className="text-[13px] text-[#656565] leading-[1.8] space-y-[2px]"
              style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              <p>Privacy Policy</p>
              <p>Terms of Service</p>
              <p>Refund Policy</p>
            </nav>
          </div>
        </div>

        {/* Copyright */}
        <p
          className="text-[11px] text-[#999] leading-[1.5]"
          style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          &copy;2025 Aday Inc. All rights reserved.
          <br />
          K-Beauty AI is for informational purposes only and is not
          <br />a substitute for professional medical advice.
        </p>
      </footer>
    </div>
  )
}
